import axios, { AxiosInstance } from "axios";
import config from "@app/config";
import { inDevelopment } from "@app/utils/env";

/** Learner session cookie, issued by core-api (`token`) and by bi-main's NextAuth writers. */
export const LEARNER_TOKEN_COOKIE = "token";
/** Admin session cookie, issued by auth-api for tenant-admin (`/admin`). */
export const ADMIN_TOKEN_COOKIE = "bi-companyToken";
/**
 * Readable, secret-free flag core-api sets beside the learner token. It exists so the app can still
 * answer "is someone signed in?" once the token itself is HttpOnly and unreadable.
 */
export const AUTH_STATE_COOKIE = "authState";

// Helper to read a cookie by name. The cookie can appear more than once (e.g. set on both the
// parent domain and the subdomain, or a stale empty one left after logout). `document.cookie`
// order is NOT reliably "most specific first" — per RFC 6265 it is sorted only by path length,
// with unspecified order among equal-length paths and no domain info at all. So pick the first
// NON-EMPTY value across all matching entries rather than blindly taking the first match.
export const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const cookieValue = document.cookie
    .split("; ")
    .filter(part => part.startsWith(`${name}=`))
    .map(part => part.slice(name.length + 1))
    .find(Boolean);
  if (!cookieValue) return null;
  try {
    return decodeURIComponent(cookieValue);
  } catch {
    return cookieValue;
  }
};

/**
 * Headers that carry the caller's session into a request made during SSR.
 *
 * `withCredentials` is a browser mechanism — Node has no cookie jar, so a call made inside
 * `getInitialProps` / `getServerSideProps` sends nothing unless the incoming request's own `Cookie`
 * header is forwarded by hand. Server-side only by design: in the browser the cookie is attached by
 * the user agent, and `Cookie` is a forbidden header there anyway.
 */
export const ssrCookieHeaders = (req?: { headers?: { cookie?: string } }): Record<string, string> => {
  if (typeof window !== "undefined") return {};
  const cookieHeader = req?.headers?.cookie;
  return cookieHeader ? { Cookie: cookieHeader } : {};
};

/**
 * Points the request at the host the app is actually being served from.
 *
 * `API_V3` is baked in at build time and names ONE host, but a single forms-front build serves every
 * tenant domain: `platform.infylearn.com/forms` and `www.boardinfinity.com/forms` are the same
 * deployment, both baked to www. So a learner on a tenant host was sending every call cross-origin
 * to www.
 *
 * That was survivable while the credential travelled in an `Authorization` header, because a header
 * crosses origins. It is not survivable on cookie auth: the session cookie is host-scoped (bi-main
 * writes it with no `domain`), so a call from an infylearn learner to www carries no credential at
 * all — and if that learner also happens to hold a www session, it carries the WRONG one.
 *
 * Every tenant host proxies the APIs under the same paths, verified against `/core-api/` on
 * platform, parul and yesmadam (.infylearn.com), exed.spjimr.org, assessments.aibm.ai,
 * ccc.idreamcareer.com, picasso and quantum (.testbi.com). So keeping the baked path and swapping
 * only the origin makes every call same-origin for whoever is filling the form. This mirrors what
 * lms-frontend's own interceptor does through `getUpdatedBaseUrl`.
 *
 * Skipped during SSR, which has no origin to read, and in local development, where localhost proxies
 * nothing and the baked remote host is the only reachable API.
 */
const rebaseToServingOrigin = (baseURL?: string): string | undefined => {
  if (!baseURL || typeof window === "undefined" || inDevelopment()) return baseURL;
  try {
    const baked = new URL(baseURL);
    if (baked.origin === window.location.origin) return baseURL;
    return `${window.location.origin}${baked.pathname}`;
  } catch {
    // Not a parseable absolute URL (a relative base, or an unset one) — leave it alone.
    return baseURL;
  }
};

/** Where the rebase stashes the env-configured base, so a failed call can fall back to it. */
const BAKED_BASE_URL = "__bakedBaseURL";
/** Set on a retried request so a second failure is reported rather than retried forever. */
export const RETRIED = "__retriedOnBakedOrigin";

/**
 * Falls back to the env-configured host when the serving origin turns out not to proxy the API.
 *
 * The rebase above assumes the host serving the form also serves the API under the same path. That
 * holds for every tenant platform host, but NOT for `forms.boardinfinity.com`, which serves this app
 * standalone and answers `/core-api/...` with the Next 404 page. core-api still hands out links on
 * that host — it is the hardcoded default in `LMS_FEEDBACK_URLS`, and the generator's host rewrite is
 * skipped on the typeform-fallback path.
 *
 * Without this, rebasing would turn "the form loads but submitting is unauthenticated" into "the form
 * does not load at all" for those links — trading a broken submit for a broken page. Authenticated
 * calls from such a host cannot work either way (the session cookie lives on the tenant host, not
 * there), so the fallback restores exactly the previous behaviour and no more.
 *
 * Narrow on purpose: only a transport failure, or a 404 whose body is HTML rather than an API
 * response, counts. A real API 404 comes back as JSON and is passed straight through.
 */
const retryOnBakedOrigin = (error: any) => {
  const requestConfig = error?.config;
  const bakedBaseURL = requestConfig?.[BAKED_BASE_URL];
  const response = error?.response;

  if (!requestConfig || !bakedBaseURL || requestConfig[RETRIED]) return Promise.reject(error);

  const servedByTheApp = response?.status === 404 && String(response?.headers?.["content-type"] || "").includes("text/html");
  if (response && !servedByTheApp) return Promise.reject(error);

  return axios.request({ ...requestConfig, baseURL: bakedBaseURL, [RETRIED]: true });
};

const handleRequest = requestConfig => {
  // Before anything else, and regardless of how this request authenticates: a cross-origin call is
  // the bug, not the auth mechanism.
  const rebased = rebaseToServingOrigin(requestConfig.baseURL);
  if (rebased !== requestConfig.baseURL) {
    requestConfig[BAKED_BASE_URL] = requestConfig.baseURL;
    requestConfig.baseURL = rebased;
  }

  // Skip if Authorization header is already set
  if (requestConfig.headers?.Authorization) return requestConfig;

  // Outside local development the session travels as a cookie only — never as a header the client
  // writes itself. That is the point of the VAPT work: a token JS can read is a token XSS can steal,
  // so there is deliberately nothing here to copy into a header. core-api's `cookieAuthFallback`
  // turns the cookie into the `Authorization` its passport strategies expect.
  //
  // Which is why the rebase above is not an optimisation but the load-bearing part: a host-scoped
  // cookie only reaches the API if the API is called on the host that holds it.
  //
  // Local development is the exception. It runs against a remote API host that this origin has no
  // cookie for, and localhost proxies nothing, so the header is the only way to authenticate there.
  if (!inDevelopment()) return requestConfig;

  // Learner routes use "token" cookie, admin routes use "bi-companyToken" cookie.
  const isAdminRoute = requestConfig.url?.includes("/tenantAdmin/");
  const isLearnerRoute = requestConfig.url?.includes("/learner/");
  // Shared /common/ and /integrations/ routes (e.g. userAuth-gated S3 multipart upload, tenant ACL)
  // need a token too — prefer the learner token, fall back to the admin token so both filling
  // contexts authenticate.
  const isSharedAuthRoute = requestConfig.url?.includes("/common/") || requestConfig.url?.includes("/integrations/");

  let token: string | null = null;
  if (isAdminRoute) token = getCookie(ADMIN_TOKEN_COOKIE);
  else if (isLearnerRoute) token = getCookie(LEARNER_TOKEN_COOKIE);
  else if (isSharedAuthRoute) token = getCookie(LEARNER_TOKEN_COOKIE) || getCookie(ADMIN_TOKEN_COOKIE);

  if (token) requestConfig.headers = { ...requestConfig.headers, Authorization: token };

  return requestConfig;
};

/**
 * Sends the auth cookie with every call — post-flip the cookie IS the credential, so a request that
 * omits it is unauthenticated.
 *
 * Unconditional, including in local development. An earlier version gated this off in dev on the
 * grounds that core-api's corsPolicy does not allow credentials, which is no longer true: it passes
 * `credentials: true`, and production answers `Access-Control-Allow-Credentials: true` with the
 * calling origin reflected back. Nothing is gained by withholding it.
 */
const credentialsConfig = { withCredentials: true };

// V3 routes instance
export const apiInstance3: AxiosInstance = axios.create({
  baseURL: config.api.v3,
  timeout: 30000,
  ...credentialsConfig
});
apiInstance3.interceptors.request.use(handleRequest);
apiInstance3.interceptors.response.use(undefined, retryOnBakedOrigin);

// V2 routes instance — same core-api host, /api/v2 base. Used for the public master-list search
// (companies / roles) that powers the job-form Company & Job Title dropdowns (as in bi-main).
const v2BaseURL = config.api.v3 ? config.api.v3.replace("/api/v3", "/api/v2") : undefined;
export const apiInstance2: AxiosInstance = axios.create({
  baseURL: v2BaseURL,
  timeout: 30000,
  ...credentialsConfig
});
apiInstance2.interceptors.request.use(handleRequest);
apiInstance2.interceptors.response.use(undefined, retryOnBakedOrigin);
